import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import Admin from "@/pages/Admin";
import Press from "@/pages/Press";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Business from "@/pages/Business";
import MiraPlusPage from "@/pages/MiraPlusPage";
import Support from "@/pages/Support";
import Demo from "@/pages/Demo";
import ColorTypeTest from "@/pages/ColorTypeTest";
import { initMetaPixel, metaPageView } from "@/lib/metaPixel";
import { initGA, gaPageView } from "@/lib/googleAnalytics";
import Feature from "@/pages/Feature";
import SeoPage, { SEO_SLUGS } from "@/pages/SeoPage";
import MenGuidePage from "@/pages/MenGuidePage";
import WomenGuidePage from "@/pages/WomenGuidePage";
import PaymentResult from "@/pages/PaymentResult";
import CookieBanner from "@/components/CookieBanner";
import ChatWidget from "@/components/ChatWidget";
import SeoTags from "@/components/SeoTags";

const UtmTracker = () => {
  const location = useLocation();
  useEffect(() => { initMetaPixel(); initGA(); }, []);
  useEffect(() => { metaPageView(); gaPageView(location.pathname); }, [location.pathname]);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const source = p.get("utm_source");
    if (!source) return;
    sessionStorage.setItem("mira-utm", JSON.stringify({ source, campaign: p.get("utm_campaign") || "" }));
    const key = `utm-${source}-${location.pathname}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source,
        medium: p.get("utm_medium") || "",
        campaign: p.get("utm_campaign") || "",
        path: location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [location]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <LanguageProvider>
      <div className="grain">
        <BrowserRouter>
          <SeoTags />
          <UtmTracker />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/impressum" element={<Legal page="impressum" />} />
            <Route path="/datenschutz" element={<Legal page="datenschutz" />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route path="/presse" element={<Press />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/stil-guide-maenner" element={<MenGuidePage />} />
            <Route path="/stil-guide-damen" element={<WomenGuidePage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/kontakt" element={<Contact />} />
            <Route path="/business" element={<Business />} />
            <Route path="/boutiquen" element={<Navigate to="/business" replace />} />
            <Route path="/mira-plus" element={<MiraPlusPage />} />
            <Route path="/plus" element={<Navigate to="/mira-plus" replace />} />
            <Route path="/pricing" element={<Navigate to="/mira-plus" replace />} />
            <Route path="/preise" element={<Navigate to="/mira-plus" replace />} />
            <Route path="/ig" element={<Navigate to="/blog?utm_source=instagram&utm_medium=bio" replace />} />
            <Route path="/support" element={<Support />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/farbtyp-test" element={<ColorTypeTest />} />
            <Route path="/go/tiktok" element={<Navigate to="/farbtyp-test?utm_source=tiktok&utm_medium=promote&utm_campaign=farbtest_nov" replace />} />
            <Route path="/features/:slug" element={<Feature />} />
            {SEO_SLUGS.map((s) => (
              <Route key={s} path={`/${s}`} element={<SeoPage slug={s} />} />
            ))}
          </Routes>
          <CookieBanner />
          <ChatWidget />
        </BrowserRouter>
        <Toaster position="bottom-center" richColors />
      </div>
    </LanguageProvider>
  );
}

export default App;
